import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';
import PageHeading from '../PageHeading/PageHeading';
import AdaptivePopup from '../../AdaptivePopup/AdaptivePopup';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import mainApi from '../../../assets/api/MainApi';
import { UserContext } from '../../../assets/contexts/userContext';
import personal_data_policy from '../../../assets/legal-docs/personal_data_policy.pdf';
import user_agreement from '../../../assets/legal-docs/user_agreement.pdf';
import {
    ADVERTISING_CAMPAIGNS_MAIN_LINK,
    PLATFORM_MAIN_LINK,
} from '../../../assets/utils/constants';
import {
    ADVERTISER_TYPE_OPTIONS,
    formatAdvertisingMoney,
    getAdvertiserTypeTitle,
    getShopDefaultUrl,
    getShopId,
    getShopName,
    parseAdvertisingApiError,
} from './advertisingCampaignsHelpers';

const INITIAL_FORM = {
    advertiser_type: 'ip',
    advertiser_name: '',
    inn: '',
    ogrn_or_ogrnip: '',
    contact_email: '',
    contact_phone: '',
    store_url: '',
    product_category: '',
    geo: '',
    comment: '',
};

const REQUIRED_FIELDS = [
    'advertiser_name',
    'inn',
    'contact_email',
    'store_url',
    'product_category',
    'geo',
];

const FORM_STEP_TARIFF = 'tariff';
const FORM_STEP_DETAILS = 'details';
const EMPTY_CATEGORY_VALUE = 'Пусто';
const CAMPAIGN_DURATION_DAYS = 28;
const TARIFF_VARIANT_MINIMAL = 'minimal';
const TARIFF_VARIANT_OPTIMAL = 'optimal';
const TARIFF_VARIANT_PREMIUM = 'premium';

const TARIFF_SALES_CONTENT = {
    [TARIFF_VARIANT_MINIMAL]: {
        badge: '',
        action: 'Выбрать минимальный',
        description: 'Базовый запуск на 28 дней: подготовим кампанию, приведем первый трафик и покажем стартовую картину по результатам.',
        points: ['Подготовим запуск', 'Приведем трафик в магазин', 'Вернем отчет по результатам'],
    },
    [TARIFF_VARIANT_OPTIMAL]: {
        badge: 'Самый выгодный',
        action: 'Выбрать оптимальный',
        description: 'Лучший баланс охвата и контроля: запускаем рекламу на 28 дней, следим за динамикой и усиливаем то, что дает отклик.',
        points: ['Проработаем аудиторию', 'Отследим статус кампании', 'Дадим выводы для роста продаж'],
    },
    [TARIFF_VARIANT_PREMIUM]: {
        badge: 'Максимум возможностей',
        action: 'Выбрать максимальный',
        description: 'Расширенный запуск на 28 дней для магазина, которому нужен заметный поток покупателей и подробная картина результата.',
        points: ['Расширим точки контакта', 'Сфокусируем бюджет на заявках', 'Подготовим детальный отчет'],
    },
};

function getCategoryTitle(category) {
    if (!category) return '';
    if (typeof category === 'string') return category;
    if (category.title?.ru) return category.title.ru;
    if (category.title) return category.title;
    if (category.name?.ru) return category.name.ru;
    if (category.name) return category.name;
    return '';
}

function getShopCategoriesText(shop, shopInfoResponse) {
    const categories = shopInfoResponse?.info?.categories || shop?.categories || shop?.info?.categories || [];
    if (!categories || categories.length === 0) return EMPTY_CATEGORY_VALUE;

    const text = categories
        .map(getCategoryTitle)
        .filter(Boolean)
        .join(', ');

    return text || EMPTY_CATEGORY_VALUE;
}

function getAdvertiserNameLabel(type) {
    if (type === 'company') return 'Название компании';
    if (type === 'individual') return 'ФИО';
    return 'Название ИП';
}

function getAdvertiserNamePlaceholder(type) {
    if (type === 'company') return 'ООО Ромашка';
    if (type === 'individual') return 'Иванов Иван Иванович';
    return 'ИП Иванов Иван Иванович';
}

function getTariffVariant(tariff) {
    const key = String(`${tariff?.code || ''} ${tariff?.title || ''}`).toLowerCase();

    if (key.includes('pro') || key.includes('max') || key.includes('максим')) return TARIFF_VARIANT_PREMIUM;
    if (key.includes('standard') || key.includes('standart') || key.includes('стандарт') || key.includes('optimal') || key.includes('оптим')) return TARIFF_VARIANT_OPTIMAL;
    if (key.includes('basic') || key.includes('min') || key.includes('миним')) return TARIFF_VARIANT_MINIMAL;

    return TARIFF_VARIANT_MINIMAL;
}

function getDefaultTariffCode(loadedTariffs) {
    return loadedTariffs.find((tariff) => getTariffVariant(tariff) === TARIFF_VARIANT_OPTIMAL)?.code
        || loadedTariffs?.[1]?.code
        || loadedTariffs?.[0]?.code
        || '';
}

function getTariffSalesContent(tariff) {
    const tariffVariant = getTariffVariant(tariff);

    if (TARIFF_SALES_CONTENT[tariffVariant]) return TARIFF_SALES_CONTENT[tariffVariant];

    return {
        badge: '',
        action: 'Выбрать тариф',
        description: tariff?.description || 'Запустим рекламную кампанию, доведем ее до результата и вернем отчет в личный кабинет.',
        points: ['Запуск кампании', 'Контроль статуса', 'Отчет по результатам'],
    };
}

function isAdvertisingFormReady({ form, currentShopId, selectedTariffCode, isTermsAccepted }) {
    const hasRequiredFields = REQUIRED_FIELDS.every((field) => String(form[field] || '').trim());
    const inn = String(form.inn || '').trim();
    const ogrn = String(form.ogrn_or_ogrnip || '').trim();
    const email = String(form.contact_email || '').trim();
    const storeUrl = String(form.store_url || '').trim();

    const isInnValid = !inn || /^\d{10,12}$/.test(inn);
    const isOgrnValid = form.advertiser_type === 'individual' || !ogrn || /^\d{13,15}$/.test(ogrn);
    const isEmailValid = !email || validator.isEmail(email);
    const isStoreUrlValid = !storeUrl || validator.isURL(storeUrl, { require_protocol: true });

    return Boolean(
        currentShopId
        && selectedTariffCode
        && isTermsAccepted
        && hasRequiredFields
        && isInnValid
        && isOgrnValid
        && isEmailValid
        && isStoreUrlValid
    );
}

function AdvertisingCampaignCreate() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [tariffs, setTariffs] = useState([]);
    const [selectedTariffCode, setSelectedTariffCode] = useState('');
    const [currentStep, setCurrentStep] = useState(FORM_STEP_TARIFF);
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const currentShop = user?.default_shop;
    const currentShopId = getShopId(currentShop);
    const selectedTariff = tariffs.find((tariff) => tariff.code === selectedTariffCode);

    useEffect(() => {
        if (!user) return undefined;

        let isCancelled = false;
        setIsLoading(true);
        setSubmitError('');

        Promise.all([
            mainApi.getAdvertisingCampaignTariffs(),
            currentShopId ? mainApi.getShopInfo({ shop_id: currentShopId }).catch(() => null) : Promise.resolve(null),
        ])
            .then(([loadedTariffs, shopInfoResponse]) => {
                if (isCancelled) return;

                const nextTariffCode = getDefaultTariffCode(loadedTariffs || []);

                setTariffs(loadedTariffs || []);
                setSelectedTariffCode(nextTariffCode);
                setForm((prevValue) => ({
                    ...prevValue,
                    contact_email: user?.email || '',
                    contact_phone: '',
                    store_url: getShopDefaultUrl(user?.default_shop),
                    product_category: getShopCategoriesText(user?.default_shop, shopInfoResponse),
                }));
            })
            .catch((err) => {
                console.log(err);
                if (!isCancelled) setSubmitError(parseAdvertisingApiError(err));
            })
            .finally(() => {
                if (!isCancelled) setIsLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [user, currentShopId]);

    function handleFormChange(evt) {
        const { name, value } = evt.target;
        setForm((prevValue) => ({
            ...prevValue,
            [name]: value,
        }));
        setSubmitError('');
        setErrors((prevValue) => ({
            ...prevValue,
            [name]: '',
        }));
    }

    function handleAdvertiserTypeChange(value) {
        setForm((prevValue) => ({
            ...prevValue,
            advertiser_type: value,
        }));
        setSubmitError('');
        setErrors((prevValue) => ({
            ...prevValue,
            ogrn_or_ogrnip: '',
        }));
    }

    function handleTariffSelect(tariffCode) {
        setSelectedTariffCode(tariffCode);
        setIsTermsAccepted(false);
        setErrors((prevValue) => ({
            ...prevValue,
            tariff_code: '',
            terms: '',
        }));
    }

    function handleTermsAcceptChange(evt) {
        setIsTermsAccepted(evt.target.checked);
        setSubmitError('');
        setErrors((prevValue) => ({
            ...prevValue,
            terms: '',
        }));
    }

    function handleNextStep() {
        const nextErrors = {};
        if (!currentShopId) nextErrors.shop_id = 'Текущий магазин не найден';
        if (!selectedTariffCode) nextErrors.tariff_code = 'Выберите тариф';

        setErrors((prevValue) => ({
            ...prevValue,
            ...nextErrors,
        }));

        if (Object.keys(nextErrors).length > 0) return;
        setSubmitError('');
        setCurrentStep(FORM_STEP_DETAILS);
        window.scrollTo(0, 0);
    }

    function handlePreviousStep() {
        setSubmitError('');
        setCurrentStep(FORM_STEP_TARIFF);
        window.scrollTo(0, 0);
    }

    function validateForm() {
        const nextErrors = {};
        let hasMissingRequiredField = false;

        if (!currentShopId) nextErrors.shop_id = 'Текущий магазин не найден';
        if (!selectedTariffCode) nextErrors.tariff_code = 'Выберите тариф';
        if (!isTermsAccepted) nextErrors.terms = 'Подтвердите согласие с условиями запуска рекламы.';

        REQUIRED_FIELDS.forEach((field) => {
            if (!String(form[field] || '').trim()) hasMissingRequiredField = true;
        });

        const inn = String(form.inn || '').trim();
        if (inn && !/^\d{10,12}$/.test(inn)) {
            nextErrors.inn = 'ИНН должен содержать 10-12 цифр';
        }

        const ogrn = String(form.ogrn_or_ogrnip || '').trim();
        if (form.advertiser_type !== 'individual' && ogrn && !/^\d{13,15}$/.test(ogrn)) {
            nextErrors.ogrn_or_ogrnip = 'ОГРН или ОГРНИП должен содержать 13-15 цифр';
        }

        const email = String(form.contact_email || '').trim();
        if (email && !validator.isEmail(email)) {
            nextErrors.contact_email = 'Введите корректный email';
        }

        const storeUrl = String(form.store_url || '').trim();
        if (storeUrl && !validator.isURL(storeUrl, { require_protocol: true })) {
            nextErrors.store_url = 'Введите ссылку с http:// или https://';
        }

        if (hasMissingRequiredField) {
            setSubmitError('Заполните обязательные поля заявки.');
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0 && !hasMissingRequiredField;
    }

    function buildRequestFormData() {
        const formData = {
            advertiser_type: form.advertiser_type,
            advertiser_name: form.advertiser_name.trim(),
            inn: form.inn.trim(),
            contact_email: form.contact_email.trim(),
            store_url: form.store_url.trim(),
            product_category: form.product_category.trim(),
            geo: form.geo.trim(),
        };

        if (form.advertiser_type !== 'individual' && form.ogrn_or_ogrnip.trim()) {
            formData.ogrn_or_ogrnip = form.ogrn_or_ogrnip.trim();
        }

        if (form.contact_phone.trim()) {
            formData.contact_phone = form.contact_phone.trim();
        }

        if (form.comment.trim()) {
            formData.comment = form.comment.trim();
        }

        return formData;
    }

    function getPaymentReturnUrl() {
        return new URL(`${PLATFORM_MAIN_LINK}/${ADVERTISING_CAMPAIGNS_MAIN_LINK}`, window.location.origin).toString();
    }

    function handleSubmit(evt) {
        evt.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);
        mainApi.createAdvertisingCampaign({
            shop_id: currentShopId,
            tariff_code: selectedTariffCode,
            form_data: buildRequestFormData(),
            return_url: getPaymentReturnUrl(),
        })
            .then((res) => {
                if (res?.promotion_id) {
                    sessionStorage.setItem('advertising_last_promotion_id', res.promotion_id);
                }

                if (res?.confirmation_url) {
                    window.location.assign(res.confirmation_url);
                    return;
                }

                if (res?.promotion_id) {
                    navigate(`../${res.promotion_id}`);
                    return;
                }

                setSubmitError('Заявка создана, но ссылка на оплату не получена. Обновите список заявок.');
            })
            .catch((err) => {
                console.log(err);
                setSubmitError(parseAdvertisingApiError(err));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }

    function renderField({ name, label, type = 'text', required = false, placeholder = '', isTextarea = false, readOnly = false }) {
        return (
            <label className={`advertising-campaigns__field ${isTextarea ? 'advertising-campaigns__field_type_textarea' : ''}`} key={name}>
                <span className='advertising-campaigns__label'>
                    {label}{required ? ' *' : ''}
                </span>
                {isTextarea ? (
                    <textarea
                        className={`advertising-campaigns__textarea ${errors[name] ? 'advertising-campaigns__input_error' : ''}`}
                        name={name}
                        value={form[name]}
                        onChange={handleFormChange}
                        placeholder={placeholder}
                    />
                ) : (
                    <input
                        className={`advertising-campaigns__input ${readOnly ? 'advertising-campaigns__input_readonly' : ''} ${errors[name] ? 'advertising-campaigns__input_error' : ''}`}
                        name={name}
                        type={type}
                        value={form[name]}
                        onChange={handleFormChange}
                        placeholder={placeholder}
                        readOnly={readOnly}
                    />
                )}
                {errors[name] ? <span className='advertising-campaigns__field-error'>{errors[name]}</span> : null}
            </label>
        );
    }

    function renderTermsDocument() {
        const tariffPrice = formatAdvertisingMoney(selectedTariff?.amount, selectedTariff?.currency);

        return (
            <div className='advertising-campaigns-create__terms-document'>
                <div className='advertising-campaigns-create__terms-document-head'>
                    <div>
                        <p className='advertising-campaigns-create__terms-kicker'>Приложение к пользовательскому соглашению</p>
                        <h4 className='advertising-campaigns-create__terms-title'>Условия оказания услуги по запуску рекламной кампании</h4>
                    </div>
                    <button
                        className='advertising-campaigns-create__terms-close'
                        type='button'
                        onClick={() => setIsTermsOpen(false)}
                        aria-label='Закрыть условия'
                    >
                        Закрыть
                    </button>
                </div>

                <div className='advertising-campaigns-create__terms-summary'>
                    <span>
                        Тариф <b>{selectedTariff?.title || '-'}</b>
                    </span>
                    <span>
                        Стоимость <b>{tariffPrice}</b>
                    </span>
                    <span>
                        Рекламный период <b>{CAMPAIGN_DURATION_DAYS} календарных дней</b>
                    </span>
                    <span>
                        Магазин <b>{getShopName(currentShop)}</b>
                    </span>
                    <span>
                        Регион <b>{form.geo || 'будет указан в заявке'}</b>
                    </span>
                    <span>
                        Рекламодатель <b>{getAdvertiserTypeTitle(form.advertiser_type)}{form.advertiser_name ? ` · ${form.advertiser_name}` : ''}</b>
                    </span>
                    <span>
                        ИНН <b>{form.inn || 'будет указан в заявке'}</b>
                    </span>
                </div>

                <div className='advertising-campaigns-create__terms-content'>
                    <section>
                        <h5>1. Статус документа и акцепт</h5>
                        <p>Настоящие условия являются специальными условиями оказания платной услуги по запуску рекламной кампании в сервисе Польза и применяются вместе с <a className='advertising-campaigns-create__terms-doc-link' href={user_agreement} target='_blank' rel='noreferrer'>Пользовательским соглашением</a>. Термины, не определенные в настоящих условиях, используются в значении, указанном в Пользовательском соглашении.</p>
                        <p>Проставление отметки согласия и создание заявки означает полный и безоговорочный акцепт настоящих условий, выбранного тарифа и стоимости услуги. С момента акцепта условия становятся обязательными для пользователя.</p>
                    </section>
                    <section>
                        <h5>2. Предмет услуги</h5>
                        <p>Сервис принимает заявку пользователя и организует работы по подготовке, запуску и сопровождению рекламной кампании для указанного магазина в течение рекламного периода <b>{CAMPAIGN_DURATION_DAYS} календарных дней</b>. Работы выполняются с учетом выбранного тарифа, ссылки на магазин, категории товаров, региона запуска, данных рекламодателя и комментариев пользователя.</p>
                    </section>
                    <section>
                        <h5>3. Тариф, цена и порядок оплаты</h5>
                        <p>Пользователь выбирает тариф <b>{selectedTariff?.title || '-'}</b>. Стоимость выбранного тарифа на момент акцепта составляет <b>{tariffPrice}</b>. Оплата производится после создания заявки через доступный в сервисе платежный сценарий. Обязанность сервиса приступить к выполнению работ возникает после подтверждения оплаты, если иной порядок не указан в интерфейсе сервиса или Пользовательском соглашении.</p>
                        <p>Если для запуска требуются дополнительные сведения, материалы, доступы или согласования, сроки подготовки и запуска исчисляются с момента получения таких сведений от пользователя. Цена тарифа включает только объем работ и рекламный период, прямо указанные в интерфейсе сервиса или настоящих условиях; дополнительные работы, бюджеты, комиссии или услуги третьих лиц оплачиваются отдельно, если они не включены в выбранный тариф.</p>
                    </section>
                    <section>
                        <h5>4. Срок рекламной кампании</h5>
                        <p>Рекламный период составляет <b>{CAMPAIGN_DURATION_DAYS} календарных дней</b> и начинается с даты фактического запуска рекламной кампании, а не с даты создания заявки, если иное не указано в интерфейсе сервиса. Подготовка заявки, проверка данных, ожидание оплаты, ожидание материалов от пользователя и модерация рекламных площадок не включаются в рекламный период.</p>
                        <p>Если кампания не может быть запущена или продолжена из-за неполных данных, недостоверных сведений, отсутствия ответов пользователя, нарушений требований законодательства, блокировок, отклонения модерации, технических ограничений рекламных площадок или иных обстоятельств вне разумного контроля сервиса, рекламный период может быть перенесен, приостановлен или завершен без ответственности сервиса за недополученные показы, переходы, заявки, заказы или выручку.</p>
                    </section>
                    <section>
                        <h5>5. Состав работ</h5>
                        <ul>
                            <li>проверка данных заявки и магазина перед запуском;</li>
                            <li>подготовка рекламной кампании под указанные товары, категорию, регион и выбранный тариф;</li>
                            <li>настройка и запуск рекламного размещения либо передача заявки в работу ответственному специалисту;</li>
                            <li>отображение статуса заявки в личном кабинете;</li>
                            <li>подготовка отчета по итогам выполненных работ в объеме, предусмотренном выбранным тарифом.</li>
                        </ul>
                    </section>
                    <section>
                        <h5>6. Обязанности пользователя</h5>
                        <p>Пользователь обязуется предоставить достоверные данные рекламодателя, корректные реквизиты, ссылку на магазин, сведения о товарах, регионе запуска и иную информацию, необходимую для оказания услуги.</p>
                        <p>Пользователь подтверждает, что является рекламодателем либо действует на законном основании от имени рекламодателя. Пользователь несет ответственность за достоверность сведений о рекламодателе, законность рекламируемых товаров, наличие необходимых лицензий, разрешений, сертификатов, прав на изображения, тексты, товарные знаки и иные материалы.</p>
                        <p>Пользователь обязуется не передавать для продвижения запрещенные товары, недостоверные сведения, вводящие в заблуждение заявления, материалы, нарушающие права третьих лиц, требования законодательства о рекламе, правила сервиса и правила рекламных площадок. Пользователь обязуется возместить сервису убытки, штрафы, претензии и расходы, возникшие из-за нарушения этих гарантий, в пределах, допускаемых применимым законодательством.</p>
                    </section>
                    <section>
                        <h5>7. Маркировка рекламы и передача данных</h5>
                        <p>Если для размещения рекламы требуется маркировка, идентификатор рекламы, указание рекламодателя, передача сведений оператору рекламных данных, рекламной площадке, платежному провайдеру или иному уполномоченному лицу, пользователь поручает сервису выполнить необходимые действия в объеме, технически доступном сервису, и дает согласие на передачу соответствующих данных.</p>
                        <p>Пользователь обязан своевременно предоставить сведения и документы, необходимые для маркировки, модерации и законного размещения рекламы. Сервис не отвечает за невозможность запуска, отклонение, блокировку или претензии, если они вызваны неполными, недостоверными или несвоевременно предоставленными данными пользователя.</p>
                    </section>
                    <section>
                        <h5>8. Модерация, отказ или приостановка</h5>
                        <p>Сервис вправе запросить уточнения, приостановить выполнение работ или отказать в запуске рекламной кампании, если данные заявки неполные или недостоверные, магазин недоступен, товары или материалы не соответствуют требованиям закона, правилам сервиса либо правилам рекламных площадок.</p>
                        <p>Если запуск невозможен по причинам, зависящим от пользователя, сроки выполнения работ продлеваются на период устранения таких причин. Если пользователь не устранил причины в разумный срок после запроса сервиса, сервис вправе завершить заявку, удержав стоимость фактически выполненных работ и понесенных расходов, если иное не предусмотрено обязательными нормами закона.</p>
                    </section>
                    <section>
                        <h5>9. Ограничение гарантий результата</h5>
                        <p>Услуга направлена на подготовку и запуск рекламной кампании для привлечения внимания целевой аудитории к магазину пользователя. Сервис не гарантирует определенное количество показов, переходов, заявок, заказов, размер выручки, окупаемость рекламы, непрерывность показа рекламы, сохранение ставок, стоимость клика или прохождение модерации внешних площадок, поскольку эти показатели зависят от спроса, региона, сезона, ассортимента, цен, состояния магазина, действий пользователя, конкуренции, настроек рекламных площадок и иных обстоятельств вне полного контроля сервиса.</p>
                    </section>
                    <section>
                        <h5>10. Отчетность и приемка</h5>
                        <p>После завершения рекламного периода или выполнения предусмотренного тарифом объема работ сервис размещает отчет или статус выполнения в личном кабинете. Отчет может содержать сведения о запуске, статусе, выполненных действиях, доступных показателях кампании и рекомендациях для дальнейшего продвижения.</p>
                        <p>Услуга считается оказанной после окончания рекламного периода, передачи отчета, изменения статуса заявки на завершенный либо предоставления пользователю иных сведений о выполненных работах, если пользователь не направил мотивированные замечания в порядке и сроки, предусмотренные Пользовательским соглашением. Замечания, связанные исключительно с отсутствием ожидаемых продаж, заявок, переходов или выручки, не являются основанием считать услугу неоказанной.</p>
                    </section>
                    <section>
                        <h5>11. Отмена, возврат и ответственность</h5>
                        <p>Пользователь вправе отказаться от услуги в порядке, предусмотренном Пользовательским соглашением и применимым законодательством. Если отказ поступил после начала подготовки, запуска, размещения или оплаты услуг третьих лиц, сервис вправе удержать стоимость фактически выполненных работ, использованного рекламного периода, оплаченных размещений, комиссий и иных подтвержденных расходов.</p>
                        <p>В максимальной степени, допустимой законом, сервис не отвечает за косвенные убытки, упущенную выгоду, потерю прибыли, снижение продаж, невозможность использования магазина, действия рекламных площадок, операторов рекламных данных, платежных провайдеров, хостингов, социальных сетей и иных третьих лиц. Совокупная ответственность сервиса по заявке ограничивается стоимостью фактически оплаченного пользователем тарифа, если иной предел ответственности не установлен обязательными нормами закона.</p>
                    </section>
                    <section>
                        <h5>12. Персональные данные и документы сервиса</h5>
                        <p>Персональные данные, указанные в заявке, обрабатываются в целях идентификации пользователя, оформления заявки, оказания услуги, оплаты, связи по заявке и исполнения требований законодательства в соответствии с <a className='advertising-campaigns-create__terms-doc-link' href={personal_data_policy} target='_blank' rel='noreferrer'>Политикой обработки персональных данных</a>.</p>
                        <p>В части, не урегулированной настоящими условиями, применяются Пользовательское соглашение, Политика обработки персональных данных и иные документы сервиса, действующие на дату акцепта.</p>
                    </section>
                    <section>
                        <h5>13. Подтверждение пользователя</h5>
                        <p>Устанавливая чекбокс согласия, пользователь подтверждает, что ознакомился с настоящими условиями, Пользовательским соглашением, Политикой обработки персональных данных, выбранным тарифом <b>{selectedTariff?.title || '-'}</b>, стоимостью <b>{tariffPrice}</b>, рекламным периодом <b>{CAMPAIGN_DURATION_DAYS} календарных дней</b> и понимает порядок оказания услуги.</p>
                    </section>
                </div>
            </div>
        );
    }

    function renderSteps() {
        return (
            <div className='advertising-campaigns-create__steps'>
                <div className={`advertising-campaigns-create__step ${currentStep === FORM_STEP_TARIFF ? 'advertising-campaigns-create__step_active' : ''}`}>
                    <span>1</span>
                    <p>Тариф</p>
                </div>
                <div className='advertising-campaigns-create__step-line' />
                <div className={`advertising-campaigns-create__step ${currentStep === FORM_STEP_DETAILS ? 'advertising-campaigns-create__step_active' : ''}`}>
                    <span>2</span>
                    <p>Заявка</p>
                </div>
            </div>
        );
    }

    function renderTariffStep() {
        return (
            <>
                <section className='advertising-campaigns-create__section advertising-campaigns-create__section_type_intro'>
                    <div className='advertising-campaigns-create__intro-copy'>
                        <span className='advertising-campaigns-create__eyebrow'>Продвижение магазина</span>
                        <h4 className='advertising-campaigns-create__intro-title'>Запустим рекламу под продажи</h4>
                        <p className='advertising-campaigns-create__intro-text'>Настроим кампанию под ваши товары, регион и целевую аудиторию, будем вести запуск и вернем отчет с выводами для роста.</p>
                    </div>
                    <div className='advertising-campaigns-create__visual' aria-label='Превью рекламного запуска'>
                        <div className='advertising-campaigns-create__visual-orbit advertising-campaigns-create__visual-orbit_type_top' />
                        <div className='advertising-campaigns-create__visual-orbit advertising-campaigns-create__visual-orbit_type_bottom' />
                        <div className='advertising-campaigns-create__visual-card advertising-campaigns-create__visual-card_type_main'>
                            <span className='advertising-campaigns-create__visual-label'>Реклама</span>
                            <p className='advertising-campaigns-create__visual-title'>{getShopName(currentShop)}</p>
                            <div className='advertising-campaigns-create__visual-bars'>
                                <span />
                                <span />
                                <span />
                                <span />
                            </div>
                            <div className='advertising-campaigns-create__visual-row'>
                                <span>Трафик</span>
                            </div>
                        </div>
                        <div className='advertising-campaigns-create__visual-card advertising-campaigns-create__visual-card_type_metric'>
                            <span>Бюджет</span>
                            <b>под контролем</b>
                        </div>
                        <div className='advertising-campaigns-create__visual-card advertising-campaigns-create__visual-card_type_report'>
                            <span>Отчет</span>
                            <b>в кабинете</b>
                        </div>
                    </div>
                    {errors.shop_id ? <span className='advertising-campaigns__field-error'>{errors.shop_id}</span> : null}
                </section>

                <section className='advertising-campaigns-create__section'>
                    <div className='advertising-campaigns-create__section-heading'>
                        <h4 className='advertising-campaigns-create__section-title'>Выберите уровень запуска</h4>
                        <p className='advertising-campaigns-create__section-subtitle'>Чем выше пакет, тем больше проработки, контроля и данных для роста продаж.</p>
                        {errors.tariff_code ? <p className='advertising-campaigns__field-error'>{errors.tariff_code}</p> : null}
                    </div>
                    <div className='advertising-campaigns-create__tariffs'>
                        {tariffs.map((tariff) => {
                            const salesContent = getTariffSalesContent(tariff);
                            const tariffVariant = getTariffVariant(tariff);
                            const isSelected = selectedTariffCode === tariff.code;

                            return (
                                <button
                                    className={`advertising-campaigns-create__tariff advertising-campaigns-create__tariff_type_${tariffVariant} ${isSelected ? 'advertising-campaigns-create__tariff_selected' : ''}`}
                                    type='button'
                                    onClick={() => handleTariffSelect(tariff.code)}
                                    aria-pressed={isSelected}
                                    key={`advertising-tariff-${tariff.code}`}
                                >
                                    <span className='advertising-campaigns-create__tariff-head'>
                                        <span className='advertising-campaigns-create__tariff-title'>{tariff.title}</span>
                                        <span
                                            className={`advertising-campaigns-create__tariff-selected-label ${isSelected ? '' : 'advertising-campaigns-create__tariff-selected-label_hidden'}`}
                                            aria-hidden={!isSelected}
                                        >
                                            Выбран
                                        </span>
                                    </span>
                                    {salesContent.badge ? <span className='advertising-campaigns-create__tariff-badge'>{salesContent.badge}</span> : null}
                                    <span className='advertising-campaigns-create__tariff-description'>{salesContent.description || tariff.description}</span>
                                    <ul className='advertising-campaigns-create__tariff-points'>
                                        {salesContent.points.map((point) => (
                                            <li key={`advertising-tariff-${tariff.code}-${point}`}>{point}</li>
                                        ))}
                                    </ul>
                                    <span className='advertising-campaigns-create__tariff-price'>{formatAdvertisingMoney(tariff.amount, tariff.currency)}</span>
                                    <span className='advertising-campaigns-create__tariff-action'>
                                        {isSelected ? 'Выбранный тариф' : salesContent.action}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </section>

                <div className='advertising-campaigns-create__actions'>
                    <button
                        className='advertising-campaigns__secondary-btn'
                        type='button'
                        onClick={() => navigate(-1)}
                    >
                        Отменить
                    </button>
                    <button
                        className='advertising-campaigns__main-btn'
                        type='button'
                        onClick={handleNextStep}
                    >
                        Продолжить
                    </button>
                </div>
            </>
        );
    }

    function renderDetailsStep() {
        const isSubmitDisabled = isSubmitting || !isAdvertisingFormReady({
            form,
            currentShopId,
            selectedTariffCode,
            isTermsAccepted,
        });

        return (
            <>
                <section className='advertising-campaigns-create__section advertising-campaigns-create__section_type_details'>
                    <div className='advertising-campaigns-create__details-head'>
                        <span className='advertising-campaigns-create__eyebrow'>Заявка на запуск</span>
                        <h4 className='advertising-campaigns-create__section-title'>Остался последний шаг</h4>
                        <p className='advertising-campaigns-create__section-subtitle'>Укажите данные для договора и регион продвижения. После оплаты команда Польза возьмет запуск в работу.</p>
                        <div className='advertising-campaigns-create__deal-strip'>
                            <span>
                                Пакет <b>{selectedTariff?.title || '-'} · {formatAdvertisingMoney(selectedTariff?.amount, selectedTariff?.currency)}</b>
                            </span>
                            <span>
                                Срок рекламы <b>{CAMPAIGN_DURATION_DAYS} дней</b>
                            </span>
                            <span>
                                Магазин <b>{getShopName(currentShop)}</b>
                            </span>
                        </div>
                        {errors.shop_id ? <span className='advertising-campaigns__field-error'>{errors.shop_id}</span> : null}
                        {errors.store_url ? <span className='advertising-campaigns__field-error'>{errors.store_url}</span> : null}
                        {errors.contact_email ? <span className='advertising-campaigns__field-error'>{errors.contact_email}</span> : null}
                    </div>

                    <div className='advertising-campaigns-create__type-block'>
                        <p className='advertising-campaigns-create__type-label'>Кто будет в договоре</p>
                        <div className='advertising-campaigns-create__type-selector'>
                            {ADVERTISER_TYPE_OPTIONS.map((type) => (
                                <button
                                    className={`advertising-campaigns-create__type ${form.advertiser_type === type.value ? 'advertising-campaigns-create__type_selected' : ''}`}
                                    type='button'
                                    onClick={() => handleAdvertiserTypeChange(type.value)}
                                    key={`advertiser-type-${type.value}`}
                                >
                                    {type.title}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='advertising-campaigns-create__fields-grid advertising-campaigns-create__fields-grid_type_simple'>
                        {renderField({
                            name: 'advertiser_name',
                            label: getAdvertiserNameLabel(form.advertiser_type),
                            required: true,
                            placeholder: getAdvertiserNamePlaceholder(form.advertiser_type),
                        })}
                        {renderField({ name: 'inn', label: 'ИНН', required: true, placeholder: '10-12 цифр' })}
                        {form.advertiser_type !== 'individual'
                            ? renderField({ name: 'ogrn_or_ogrnip', label: 'ОГРН / ОГРНИП', placeholder: 'Если хотите указать' })
                            : null}
                        {renderField({ name: 'geo', label: 'Регион запуска', required: true, placeholder: 'Москва и область' })}
                        {renderField({ name: 'comment', label: 'Что важно учесть', isTextarea: true, placeholder: 'Акции, сезонность, приоритетные товары' })}
                    </div>

                    <div className='advertising-campaigns-create__terms-check'>
                        <label className='advertising-campaigns-create__terms-check-control'>
                            <input
                                type='checkbox'
                                checked={isTermsAccepted}
                                onChange={handleTermsAcceptChange}
                            />
                            <span className='advertising-campaigns-create__terms-check-box' aria-hidden='true' />
                        </label>
                        <p className='advertising-campaigns-create__terms-check-text'>
                            Я ознакомился и согласен с{' '}
                            <button
                                type='button'
                                className='advertising-campaigns-create__terms-link'
                                onClick={() => setIsTermsOpen(true)}
                            >
                                условиями запуска рекламы
                            </button>
                            , включая выбранный тариф, стоимость услуги и рекламный период {CAMPAIGN_DURATION_DAYS} календарных дней.
                        </p>
                    </div>
                    {errors.terms ? <span className='advertising-campaigns__field-error'>{errors.terms}</span> : null}
                </section>

                {submitError ? <p className='advertising-campaigns__error'>{submitError}</p> : null}

                <div className='advertising-campaigns-create__actions advertising-campaigns-create__actions_type_details'>
                    <button
                        className='advertising-campaigns__secondary-btn'
                        type='button'
                        onClick={handlePreviousStep}
                        disabled={isSubmitting}
                    >
                        Назад к тарифам
                    </button>
                    <button
                        className='advertising-campaigns__main-btn'
                        type='submit'
                        disabled={isSubmitDisabled}
                    >
                        {isSubmitting ? <MiniPreloader isLinkColor={true} /> : 'Создать и перейти к оплате'}
                    </button>
                </div>
            </>
        );
    }

    return (
        <div className='advertising-campaigns-create'>
            <PageHeading
                className='advertising-campaigns-create__heading'
                title='Ведение рекламы'
                goBack={{
                    onClick: () => navigate(-1),
                }}
            />

            {isLoading ? (
                <div className='advertising-campaigns__preloader'>
                    <MiniPreloader />
                </div>
            ) : (
                <form className='advertising-campaigns-create__form' onSubmit={handleSubmit}>
                    {renderSteps()}
                    {currentStep === FORM_STEP_TARIFF ? renderTariffStep() : renderDetailsStep()}
                </form>
            )}

            <AdaptivePopup
                isOpen={isTermsOpen}
                handleClose={() => setIsTermsOpen(false)}
                className='advertising-campaigns-create__terms-popup'
            >
                {renderTermsDocument()}
            </AdaptivePopup>
        </div>
    );
}

export default AdvertisingCampaignCreate;
